import type { StepEntity, VOnboardingWrapperOptions } from 'v-onboarding'
import { defineStore } from 'pinia'
import storage from '~/foundation/storage/localstorage'

export interface RegisterTourPayload {
  id: string
  steps: StepEntity[]
  options?: VOnboardingWrapperOptions
  /**
   * Menor = maior prioridade (ex.: header 0, páginas 10+)
   */
  priority?: number
  /**
   * Chave de persistência no localStorage. Default: `onboarding:<id>:completed`
   */
  storageKey?: string
}

export interface OnboardingTour {
  id: string
  steps: StepEntity[]
  options?: VOnboardingWrapperOptions
  priority: number
  storageKey: string
}

export interface OnboardingState {
  tours: Record<string, OnboardingTour>
  queue: string[]
  activeTourId: string | null
}

function getDefaultStorageKey(tourId: string) {
  return `onboarding:${tourId}:completed`
}

export const useOnboardingStore = defineStore('onboarding-store', {
  state: (): OnboardingState => ({
    tours: {},
    queue: [],
    activeTourId: null,
  }),

  getters: {
    activeTour(state): OnboardingTour | null {
      return state.activeTourId ? state.tours[state.activeTourId] ?? null : null
    },
    activeSteps(): StepEntity[] {
      return this.activeTour?.steps ?? []
    },
    activeOptions(): VOnboardingWrapperOptions | undefined {
      return this.activeTour?.options
    },
    isActive(state): boolean {
      return Boolean(state.activeTourId)
    },
  },

  actions: {
    isTourCompleted(tourId: string) {
      const tour = this.tours[tourId]
      const storageKey = tour?.storageKey ?? getDefaultStorageKey(tourId)
      return storage.get(storageKey) === 'true'
    },

    resetTour(tourId: string) {
      const tour = this.tours[tourId]
      const storageKey = tour?.storageKey ?? getDefaultStorageKey(tourId)
      storage.remove(storageKey)
    },

    markTourCompleted(tourId: string) {
      const tour = this.tours[tourId]
      const storageKey = tour?.storageKey ?? getDefaultStorageKey(tourId)
      storage.set(storageKey, 'true')
    },

    registerTour(payload: RegisterTourPayload) {
      const storageKey = payload.storageKey ?? getDefaultStorageKey(payload.id)
      const priority = payload.priority ?? 100

      this.tours[payload.id] = {
        id: payload.id,
        steps: payload.steps,
        options: payload.options,
        priority,
        storageKey,
      }
    },

    unregisterTour(tourId: string) {
      delete this.tours[tourId]
      this.queue = this.queue.filter(id => id !== tourId)

      if (this.activeTourId === tourId) {
        this.activeTourId = null
        this.startNextTour()
      }
    },

    requestTour(tourId: string) {
      if (!this.tours[tourId]) {
        return
      }

      if (this.isTourCompleted(tourId)) {
        return
      }

      if (this.activeTourId === tourId) {
        return
      }

      if (!this.queue.includes(tourId)) {
        this.queue.push(tourId)
      }

      this.startNextTour()
    },

    startNextTour() {
      if (this.activeTourId) {
        return
      }

      const nextTourId = this.queue
        .filter(id => Boolean(this.tours[id]) && !this.isTourCompleted(id))
        .sort((firstId, secondId) => {
          const firstPriority = this.tours[firstId]?.priority ?? 100
          const secondPriority = this.tours[secondId]?.priority ?? 100
          return firstPriority - secondPriority
        })[0]

      if (!nextTourId) {
        return
      }

      this.activeTourId = nextTourId
    },

    finishActiveTour() {
      const tourId = this.activeTourId
      if (!tourId) {
        return
      }

      this.markTourCompleted(tourId)
      this.queue = this.queue.filter(id => id !== tourId)
      this.activeTourId = null
      this.startNextTour()
    },
  },
})
